<template>
  <div class="json-editor">
    <div
      ref="editorContainer"
      class="json-editor__container"
      :class="{
        'json-editor__container--disabled': disabled,
        'json-editor__container--drag-over': isDragOver
      }"
      @dragover.prevent.stop="handleDragOver"
      @dragenter.prevent.stop="handleDragEnter"
      @dragleave.prevent.stop="handleDragLeave"
      @drop.prevent.stop="handleDrop"
    >
      <!-- CodeMirror will be mounted here -->
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import type { CassieField } from './index'

// CodeMirror imports
import CodeMirror from 'codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/javascript/javascript' // This includes JSON mode
import 'codemirror/addon/edit/matchbrackets'
import 'codemirror/addon/edit/closebrackets'
import 'codemirror/addon/selection/active-line'
import 'codemirror/addon/fold/foldcode'
import 'codemirror/addon/fold/foldgutter'
import 'codemirror/addon/fold/brace-fold'
import 'codemirror/addon/display/placeholder'
import 'codemirror/addon/lint/lint'
import 'codemirror/addon/lint/lint.css'
import 'codemirror/addon/lint/json-lint'
import 'codemirror/theme/material-darker.css'
import 'codemirror/theme/dracula.css'

/**
 * Unscoped CodeMirror height overrides injected via Constructable Stylesheet
 * (CSP-compliant — no inline <style> element). Shared across all instances.
 */
let jsonEditorStyleSheet: CSSStyleSheet | null = null
function ensureJsonEditorStyles (): void {
  if (jsonEditorStyleSheet) return
  jsonEditorStyleSheet = new CSSStyleSheet()
  jsonEditorStyleSheet.replaceSync(
    '.json-editor .CodeMirror-scroll { min-height: 400px !important; height: auto !important; font-family: "Consolas", "Monaco", "Lucida Console", "Liberation Mono", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Courier New", monospace !important; }' +
    '.json-editor .CodeMirror { min-height: 400px !important; }'
  )
  document.adoptedStyleSheets = ([] as CSSStyleSheet[]).concat(document.adoptedStyleSheets || [], [jsonEditorStyleSheet])
}

export default Vue.extend({
  name: 'JsonEditor',
  props: {
    value: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Enter your JSON template here...'
    },
    dragField: {
      type: Object as () => CassieField | null,
      default: null
    },
    theme: {
      type: String,
      default: 'default',
      validator: (value: string) => ['default', 'material-darker', 'dracula'].includes(value)
    },
    availableFields: {
      type: Array as () => CassieField[],
      default: () => []
    },
    showTokenChips: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editor: null as CodeMirror.Editor | null,
      isDragOver: false,
      isInitialized: false,
      tokenMarkers: [] as any[],
      tokenRefreshTimeout: null as any,
      cursorActivityTimeout: null as any
    }
  },
  methods: {
    /**
     * Initialize CodeMirror editor
     */
    initializeEditor(): void {
      if (this.isInitialized || !this.$refs.editorContainer) return

      // Define custom mode first, before creating the editor
      this.defineCustomMode()

      // Determine the mode to use - fallback to javascript if custom mode fails
      let editorMode = 'javascript' // Safe fallback
      try {
        if (CodeMirror.modes && CodeMirror.modes['json-with-tokens']) {
          editorMode = 'json-with-tokens'
        } else if (CodeMirror.getMode({}, 'application/json')) {
          editorMode = 'application/json'
        }
      } catch (e) {
        console.warn('Failed to determine CodeMirror mode, using javascript fallback:', e)
      }

      this.editor = CodeMirror(this.$refs.editorContainer as HTMLElement, {
        value: this.value,
        mode: editorMode,
        theme: this.theme,
        lineNumbers: true,
        lineWrapping: true,
        matchBrackets: false,
        autoCloseBrackets: false,
        styleActiveLine: true,
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', 'CodeMirror-lint-markers'],
        placeholder: this.placeholder,
        indentUnit: 2,
        tabSize: 2,
        indentWithTabs: false,
        readOnly: this.disabled,
        lint: {
          getAnnotations: this.customJsonLint,
          async: false
        },
        extraKeys: {
          'Ctrl-Space': 'autocomplete',
          'Ctrl-Shift-F': () => this.formatJson(),
          'Tab': (cm: CodeMirror.Editor) => {
            if (cm.somethingSelected()) {
              cm.indentSelection('add')
            } else {
              cm.replaceSelection('  ')
            }
          }
        }
      })

      // Set up event listeners
      this.setupEventListeners()

      // Unscoped height overrides — scoped :deep() styles lose the
      // specificity battle against CodeMirror's own rules.
      ensureJsonEditorStyles()

      // Force refresh highlighting after setup
      this.$nextTick(() => {
        this.refreshHighlighting()
        // Force initial token widget creation only if chips are enabled
        if (this.showTokenChips) {
          setTimeout(() => {
            this.refreshTokenWidgets()
          }, 500)
        }
      })

      this.isInitialized = true
    },

    /**
     * Custom JSON linting that handles field tokens
     */
    customJsonLint(text: string): Array<{
      message: string
      severity: 'error' | 'warning'
      from: CodeMirror.Position
      to: CodeMirror.Position
    }> {
      const annotations: Array<{
        message: string
        severity: 'error' | 'warning'
        from: CodeMirror.Position
        to: CodeMirror.Position
      }> = []
      
      if (!text.trim()) return annotations
      
      try {
        // Use our comprehensive token replacement function
        const testText = this.replaceTokensForValidation(text)
        
        // Try to parse the JSON
        JSON.parse(testText)
        
        // If parsing succeeds, check for potential issues (but be more lenient with tokens)
        this.validateJsonStructure(text, annotations)
        
      } catch (error) {
        // If there's still an error, check if it's related to tokens
        const errorMessage = error instanceof Error ? error.message : String(error)
        const errorMatch = errorMessage.match(/at position (\d+)/)
        
        if (errorMatch) {
          const position = parseInt(errorMatch[1], 10)
          
          // Check if the error is near a token - if so, ignore it
          if (this.isNearToken(text, position)) {
            return annotations // Don't show errors near tokens
          }
          
          const lines = text.substring(0, position).split('\n')
          const line = lines.length - 1
          const column = lines[lines.length - 1].length
          
          annotations.push({
            message: this.formatJsonError(errorMessage),
            severity: 'error',
            from: CodeMirror.Pos(line, column),
            to: CodeMirror.Pos(line, column + 1)
          })
        } else {
          // Only show fallback error if it's not related to tokens
          if (!text.includes('[PLACEHOLDER_') && !text.includes('{{')) {
            annotations.push({
              message: this.formatJsonError(errorMessage),
              severity: 'error',
              from: CodeMirror.Pos(0, 0),
              to: CodeMirror.Pos(0, 1)
            })
          }
        }
      }
      
      return annotations
    },

    /**
     * Format JSON error messages for better readability
     */
    formatJsonError(message: string): string {
      return message
        .replace(/JSON.parse: /, '')
        .replace(/Unexpected token/, 'Unexpected character')
        .replace(/at position \d+/, '')
        .trim()
    },

    /**
     * Validate JSON structure and add warnings
     */
    validateJsonStructure(text: string, annotations: Array<{
      message: string
      severity: 'error' | 'warning'
      from: CodeMirror.Position
      to: CodeMirror.Position
    }>): void {
      const lines = text.split('\n')
      
      // Check for common issues but be lenient with tokens
      lines.forEach((line, index) => {
        // Skip lines that contain tokens
        if (line.includes('[PLACEHOLDER_') || line.includes('{{')) {
          return
        }
        
        // Check for trailing commas
        if (line.trim().match(/,\s*[}\]]/)) {
          annotations.push({
            message: 'Trailing comma detected',
            severity: 'warning',
            from: CodeMirror.Pos(index, line.lastIndexOf(',')),
            to: CodeMirror.Pos(index, line.lastIndexOf(',') + 1)
          })
        }
        
        // Check for unquoted keys (but only if no tokens nearby)
        if (line.match(/^\s*[a-zA-Z_$][a-zA-Z0-9_$]*\s*:/)) {
          const match = line.match(/^\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/)
          if (match) {
            // Check if there's a token in the value part
            const valuepart = line.substring(line.indexOf(':') + 1)
            if (!valuepart.includes('[PLACEHOLDER_') && !valuepart.includes('{{')) {
              annotations.push({
                message: 'Object keys should be quoted',
                severity: 'warning',
                from: CodeMirror.Pos(index, line.indexOf(match[1])),
                to: CodeMirror.Pos(index, line.indexOf(match[1]) + match[1].length)
              })
            }
          }
        }
      })
    },

    /**
     * Define custom highlighting mode for field tokens
     */
    defineCustomMode(): void {
      // Check if the mode is already defined to avoid redefinition
      if (CodeMirror.modes && CodeMirror.modes['json-with-tokens']) {
        return
      }
      
      // Define custom mode for enhanced JSON with token highlighting
      CodeMirror.defineMode('json-with-tokens', (config) => {
        // Get the base JSON mode
        const jsonMode = CodeMirror.getMode(config, 'application/json')
        
        // Guard against undefined jsonMode
        if (!jsonMode) {
          console.warn('JSON mode not available, falling back to basic mode')
          return CodeMirror.getMode(config, 'javascript')
        }
        
        return {
          startState: () => ({ 
            base: CodeMirror.startState(jsonMode),
            inString: false,
            stringDelim: null
          }),
          token: (stream, state) => {
            // Check if we're inside a string
            if (state.inString) {
              // Check for tokens inside strings - handle each token separately
              if (stream.match(/\[PLACEHOLDER_[A-Z0-9_]+\]/)) {
                return 'field-token'
              }
              
              if (stream.match(/{{\s*[\w.\[\]_-]+\s*}}/)) {
                return 'field-token'
              }
              
              // Check for end of string
              if (stream.peek() === state.stringDelim) {
                stream.next()
                state.inString = false
                state.stringDelim = null
                return 'string'
              }
              
              // Advance only one character at a time to ensure we catch individual tokens
              stream.next()
              return 'string'
            }
            
            // Check for start of string
            if (stream.peek() === '"' || stream.peek() === "'") {
              state.inString = true
              state.stringDelim = stream.peek()
              stream.next()
              return 'string'
            }
            
            // Check for tokens outside strings (unquoted)
            if (stream.match(/\[PLACEHOLDER_[A-Z0-9_]+\]/)) {
              return 'field-token'
            }
            
            if (stream.match(/{{\s*[\w.\[\]_-]+\s*}}/)) {
              return 'field-token'
            }
            
            // Use base JSON mode for everything else
            return jsonMode.token(stream, state.base)
          },
          innerMode: (state: any) => ({ mode: jsonMode, state: state.base })
        }
      })
    },

    /**
     * Add token widget as a chip
     */
    addTokenWidget(tokenText: string): void {
      if (!this.editor) return
      
      try {
        const cursor = this.editor.getCursor()
        const line = cursor.line
        const ch = cursor.ch
        
        // Find all occurrences of the token in the current line
        const lineContent = this.editor.getLine(line)
        if (!lineContent) return
        
        const tokenIndex = lineContent.lastIndexOf(tokenText, ch)
        if (tokenIndex === -1) return
        
        // Create chip element
        const chipElement = document.createElement('span')
        chipElement.className = 'json-token-chip'
        chipElement.textContent = this.formatTokenForDisplay(tokenText)
        chipElement.setAttribute('data-token', tokenText)
        
        // Add click handler to allow editing
        chipElement.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          this.editToken(line, tokenIndex, tokenText)
        })
        
        // Create widget at token position
        const from = CodeMirror.Pos(line, tokenIndex)
        const to = CodeMirror.Pos(line, tokenIndex + tokenText.length)
        
        // Mark the text range and replace with widget
        const marker = this.editor.markText(from, to, {
          replacedWith: chipElement,
          handleMouseEvents: true,
          clearOnEnter: false
        })
        
        // Store marker reference for cleanup
        if (!this.tokenMarkers) {
          this.tokenMarkers = []
        }
        this.tokenMarkers.push(marker)
        
      } catch (error) {
        console.warn('Failed to create token widget:', error)
      }
    },

    /**
     * Format token text for display in chip
     */
    formatTokenForDisplay(tokenText: string): string {
      // Extract field name from token
      if (tokenText.includes('[PLACEHOLDER_')) {
        const fieldName = tokenText.replace(/\[PLACEHOLDER_|]/g, '')
        return fieldName.toLowerCase().replace(/_/g, ' ')
      }
      
      if (tokenText.includes('{{') && tokenText.includes('}}')) {
        return tokenText.replace(/{{|}}/g, '').trim()
      }
      
      return tokenText
    },

    /**
     * Handle token editing
     */
    editToken(line: number, startCh: number, tokenText: string): void {
      if (!this.editor) return
      
      // Replace chip with editable text temporarily
      const endCh = startCh + tokenText.length
      this.editor.setSelection(CodeMirror.Pos(line, startCh), CodeMirror.Pos(line, endCh))
      this.editor.focus()
    },

    /**
     * Clear all token markers
     */
    clearTokenMarkers(): void {
      if (this.tokenMarkers) {
        this.tokenMarkers.forEach(marker => {
          try {
            marker.clear()
          } catch (e) {
            // Marker may already be cleared
          }
        })
        this.tokenMarkers = []
      }
    },

    /**
     * Get field icon based on type
     */
    getFieldIcon(typeFriendlyName: string): string {
      const iconMap: Record<string, string> = {
        text: 'mdi-text',
        number: 'mdi-numeric',
        date: 'mdi-calendar',
        boolean: 'mdi-toggle-switch',
        guid: 'mdi-identifier',
        string: 'mdi-text',
        integer: 'mdi-numeric',
        decimal: 'mdi-numeric',
        float: 'mdi-numeric',
        double: 'mdi-numeric',
        int: 'mdi-numeric',
        long: 'mdi-numeric',
        short: 'mdi-numeric',
        byte: 'mdi-numeric',
        bool: 'mdi-toggle-switch',
        datetime: 'mdi-calendar-clock',
        timestamp: 'mdi-calendar-clock',
        time: 'mdi-clock-outline',
        uuid: 'mdi-identifier',
        email: 'mdi-email-outline',
        url: 'mdi-link-variant',
        phone: 'mdi-phone-outline',
        varchar: 'mdi-text',
        char: 'mdi-text',
        nvarchar: 'mdi-text',
        nchar: 'mdi-text',
        binary: 'mdi-file-outline',
        varbinary: 'mdi-file-outline',
        image: 'mdi-image-outline',
        xml: 'mdi-code-tags',
        json: 'mdi-code-braces'
      }

      const type = typeFriendlyName?.toLowerCase() || 'text'
      return iconMap[type] || 'mdi-database'
    },

    /**
     * Get field object from field name for icon display
     */
    getFieldFromName(fieldName: string): CassieField | null {
      if (!this.availableFields || this.availableFields.length === 0) {
        return null
      }
      
      return this.availableFields.find(field => {
        const cleanFieldName = field.fieldName
          .replace(/[^a-zA-Z0-9_]/g, '')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
          .toUpperCase()
        
        return cleanFieldName === fieldName.toUpperCase()
      }) || null
    },

    /**
     * Check if a placeholder token corresponds to a valid field
     */
    isValidPlaceholder(tokenText: string): boolean {
      // If no available fields are provided, show all chips (backward compatibility)
      if (!this.availableFields || this.availableFields.length === 0) {
        return true
      }
      
      // Extract field name from token
      let fieldName = ''
      if (tokenText.includes('[PLACEHOLDER_')) {
        fieldName = tokenText.replace(/\[PLACEHOLDER_|]/g, '')
      } else if (tokenText.includes('{{') && tokenText.includes('}}')) {
        fieldName = tokenText.replace(/{{|}}/g, '').trim()
      }
      
      if (!fieldName) return false
      
      // Check if this field name matches any of the available fields
      return this.availableFields.some(field => {
        const cleanFieldName = field.fieldName
          .replace(/[^a-zA-Z0-9_]/g, '')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
          .toUpperCase()
        
        return cleanFieldName === fieldName.toUpperCase()
      })
    },

    /**
     * Refresh token widgets
     */
    refreshTokenWidgets(): void {
      this.clearTokenMarkers()
      
      if (!this.editor || !this.showTokenChips) return
      
      // Re-scan document for tokens and create widgets
      const content = this.editor.getValue()
      const lines = content.split('\n')
      
      lines.forEach((line, lineIndex) => {
        // Find all tokens in this line
        const tokenRegex = /(\[PLACEHOLDER_[A-Z0-9_]+\]|{{\s*[\w.\[\]_-]+\s*}})/g
        let match
        
        while ((match = tokenRegex.exec(line)) !== null) {
          const tokenText = match[1]
          const startCh = match.index
          
          // Only create chips for valid placeholders
          if (!this.isValidPlaceholder(tokenText)) {
            continue
          }
          
          
          // Create widget using CodeMirror's markText with replacedWith
          const from = CodeMirror.Pos(lineIndex, startCh)
          const to = CodeMirror.Pos(lineIndex, startCh + tokenText.length)
          
          try {
            // Get field info for icon
            let fieldName = ''
            if (tokenText.includes('[PLACEHOLDER_')) {
              fieldName = tokenText.replace(/\[PLACEHOLDER_|\]/g, '')
            } else if (tokenText.includes('{{') && tokenText.includes('}}')) {
              fieldName = tokenText.replace(/{{|}}}/g, '').trim()
            }
            
            const field = this.getFieldFromName(fieldName)
            const fieldIcon = field ? this.getFieldIcon(field.typeFriendlyName) : 'mdi-database'
            
            // Create the chip element with icon
            const chipElement = document.createElement('span')
            chipElement.className = 'json-token-chip'
            chipElement.setAttribute('data-token', tokenText)
            
            // Create icon element
            const iconElement = document.createElement('span')
            iconElement.className = 'mdi ' + fieldIcon + ' field-chip__icon'
            iconElement.style.setProperty('color', '#ffffff', 'important')
            iconElement.style.setProperty('font-size', '12px', 'important')
            iconElement.style.setProperty('margin-right', '4px', 'important')
            
            // Create text element
            const textElement = document.createElement('span')
            textElement.className = 'field-chip__name'
            textElement.textContent = this.formatTokenForDisplay(tokenText)
            textElement.style.setProperty('font-size', '13px', 'important')
            textElement.style.setProperty('font-weight', '500', 'important')
            textElement.style.setProperty('color', '#ffffff', 'important')
            
            // Append icon and text to chip
            chipElement.appendChild(iconElement)
            chipElement.appendChild(textElement)
            
            // Set chip styling to match DraggableFieldChip
            chipElement.style.setProperty('display', 'inline-flex', 'important')
            chipElement.style.setProperty('align-items', 'center', 'important')
            chipElement.style.setProperty('cursor', 'grab', 'important')
            chipElement.style.setProperty('transition', 'all 0.2s ease', 'important')
            chipElement.style.setProperty('border', 'none', 'important')
            chipElement.style.setProperty('color', '#ffffff', 'important')
            chipElement.style.setProperty('background', '#406A94', 'important')
            chipElement.style.setProperty('border-radius', 'var(--radius-lg)', 'important')
            chipElement.style.setProperty('height', '24px', 'important')
            chipElement.style.setProperty('box-shadow', '0 1px 3px rgba(0, 0, 0, 0.08)', 'important')
            chipElement.style.setProperty('padding', '2px 8px', 'important')
            chipElement.style.setProperty('margin', '0 2px', 'important')
            chipElement.style.setProperty('white-space', 'nowrap', 'important')
            chipElement.style.setProperty('position', 'relative', 'important')
            chipElement.style.setProperty('z-index', '999', 'important')
            chipElement.style.setProperty('font-family', 'inherit', 'important')
            chipElement.style.setProperty('line-height', '1.2', 'important')
            chipElement.style.setProperty('vertical-align', 'middle', 'important')
            
            // Add click handler
            chipElement.addEventListener('click', (e) => {
              e.preventDefault()
              e.stopPropagation()
              this.editToken(lineIndex, startCh, tokenText)
            })
            
            // Use markText with replacedWith to actually replace the text
            const marker = this.editor.markText(from, to, {
              replacedWith: chipElement,
              handleMouseEvents: true,
              clearOnEnter: false,
              atomic: true,
              inclusiveLeft: false,
              inclusiveRight: false
            })
            
            if (marker) {
              if (!this.tokenMarkers) {
                this.tokenMarkers = []
              }
              this.tokenMarkers.push(marker)
            } else {
              console.warn('Failed to create marker for:', tokenText)
            }
            
          } catch (error) {
            console.warn('Failed to create token widget:', error)
            
            // Fallback: at least add CSS class to the token
            try {
              const marker = this.editor.markText(from, to, {
                className: 'json-token-chip-fallback',
                clearOnEnter: false
              })
              
              if (!this.tokenMarkers) {
                this.tokenMarkers = []
              }
              this.tokenMarkers.push(marker)
            } catch (fallbackError) {
              console.warn('Even fallback failed:', fallbackError)
            }
          }
        }
      })
      
      // Force editor refresh to ensure widgets are rendered
      this.$nextTick(() => {
        this.editor.refresh()
      })
    },

    /**
     * Add custom highlighting for field tokens
     */
    addTokenHighlighting(): void {
      // This method is now handled in defineCustomMode and initializeEditor
      // Keeping for backward compatibility but it's no longer needed
    },

    /**
     * Set up CodeMirror event listeners
     */
    setupEventListeners(): void {
      if (!this.editor) return

      // Handle value changes
      this.editor.on('change', (instance: CodeMirror.Editor) => {
        const newValue = instance.getValue()
        this.$emit('input', newValue)
        
        // Refresh token widgets after a short delay to allow text changes to settle
        clearTimeout(this.tokenRefreshTimeout)
        this.tokenRefreshTimeout = setTimeout(() => {
          if (this.showTokenChips) {
            this.refreshTokenWidgets()
          }
        }, 200)
      })

      // Handle focus events
      this.editor.on('focus', () => {
        this.$emit('focus')
      })

      this.editor.on('blur', () => {
        this.$emit('blur')
      })

      // Handle cursor activity to refresh widgets when needed
      this.editor.on('cursorActivity', () => {
        // Debounce widget refresh
        clearTimeout(this.cursorActivityTimeout)
        this.cursorActivityTimeout = setTimeout(() => {
          if (this.showTokenChips) {
            this.refreshTokenWidgets()
          }
        }, 300)
      })
    },

    /**
     * Handle drag over
     */
    handleDragOver(event: any): void {
      if (this.disabled || !this.dragField) {
        event.preventDefault()
        return
      }
      
      event.preventDefault()
      event.stopPropagation()
      
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy'
      }
      
      this.isDragOver = true
    },

    /**
     * Handle drag enter
     */
    handleDragEnter(event: any): void {
      if (this.disabled || !this.dragField) {
        event.preventDefault()
        return
      }
      
      event.preventDefault()
      event.stopPropagation()
      this.isDragOver = true
    },

    /**
     * Handle drag leave
     */
    handleDragLeave(event: any): void {
      if (this.disabled || !this.dragField) {
        event.preventDefault()
        return
      }
      
      event.preventDefault()
      event.stopPropagation()
      this.isDragOver = false
    },

    /**
     * Generate clean field token from field name
     */
    generateFieldToken(field: CassieField): string {
      // Use the fieldName directly, just clean it up
      const cleanFieldName = field.fieldName
        .replace(/[^a-zA-Z0-9_]/g, '') // Remove any non-alphanumeric/underscore characters
        .replace(/_+/g, '_') // Replace multiple underscores with single underscore
        .replace(/^_|_$/g, '') // Remove leading/trailing underscores
        .toUpperCase()
      
      return `[PLACEHOLDER_${cleanFieldName}]`
    },

    /**
     * Handle field drop
     */
    handleDrop(event: any): void {
      if (this.disabled || !this.dragField || !this.editor) return
      
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      
      this.isDragOver = false
      
      // Get cursor position from mouse coordinates
      const coords = this.editor.coordsChar({
        left: event.clientX,
        top: event.clientY
      })
      
      // Generate field token
      const token = this.generateFieldToken(this.dragField)
      
      // Clear any selection before inserting
      if (this.editor.somethingSelected()) {
        this.editor.replaceSelection('')
      }
      
      // Insert token at drop position
      this.editor.replaceRange(token, coords)
      
      // Focus and position cursor after token
      this.editor.focus()
      this.editor.setCursor({
        line: coords.line,
        ch: coords.ch + token.length
      })
      
      this.$emit('drop', this.dragField, { position: coords })
    },

    /**
     * Insert field token at current cursor position
     */
    insertFieldToken(field: CassieField): void {
      if (this.disabled || !this.editor) return
      
      const token = this.generateFieldToken(field)
      
      const cursor = this.editor.getCursor()
      this.editor.replaceRange(token, cursor)
      
      // Position cursor after token
      this.editor.setCursor({
        line: cursor.line,
        ch: cursor.ch + token.length
      })
      
      this.editor.focus()
    },

    /**
     * Format JSON content
     */
    formatJson(): void {
      if (this.disabled || !this.editor) return
      
      try {
        const content = this.editor.getValue()
        if (!content.trim()) return
        
        // Replace tokens with placeholders for formatting
        const tokenMap: Record<string, string> = {}
        let tokenIndex = 0
        
        let withPlaceholders = content
        
        // More careful token replacement to avoid double-quoting
        // First, replace tokens that are already inside quotes
        withPlaceholders = withPlaceholders.replace(/"[^"]*\[PLACEHOLDER_[\w_]+\][^"]*"/g, (match: string) => {
          // For tokens inside quoted strings, replace each token individually
          return match.replace(/\[PLACEHOLDER_[\w_]+\]/g, (tokenMatch: string) => {
            const placeholder = `__TOKEN_${tokenIndex++}__`
            tokenMap[placeholder] = tokenMatch
            return placeholder
          })
        })
        
        // Then replace tokens that are not inside quotes (unquoted property values)
        withPlaceholders = withPlaceholders.replace(/\[PLACEHOLDER_[\w_]+\]/g, (match: string) => {
          const placeholder = `__TOKEN_${tokenIndex++}__`
          tokenMap[placeholder] = match
          return `"${placeholder}"`
        })
        
        // Handle legacy {{fieldName}} tokens inside quotes
        withPlaceholders = withPlaceholders.replace(/"[^"]*{{\s*[\w.\[\]_-]+\s*}}[^"]*"/g, (match: string) => {
          return match.replace(/{{\s*([\w.\[\]_-]+)\s*}}/g, (tokenMatch: string) => {
            const placeholder = `__TOKEN_${tokenIndex++}__`
            tokenMap[placeholder] = tokenMatch
            return placeholder
          })
        })
        
        // Handle legacy {{fieldName}} tokens not inside quotes
        withPlaceholders = withPlaceholders.replace(/{{\s*([\w.\[\]_-]+)\s*}}/g, (match: string) => {
          const placeholder = `__TOKEN_${tokenIndex++}__`
          tokenMap[placeholder] = match
          return `"${placeholder}"`
        })
        
        // Clean up any malformed JSON before parsing
        withPlaceholders = withPlaceholders
          .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
          .replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3') // Quote unquoted keys
        
        // Validate that we have valid JSON before attempting to parse
        let parsed: any
        try {
          parsed = JSON.parse(withPlaceholders)
        } catch (parseError) {
          console.warn('JSON parsing failed during formatting:', parseError)
          console.warn('Processed content:', withPlaceholders)
          
          // Try to extract valid JSON from the beginning of the content
          let jsonStart = withPlaceholders.trim()
          let validJson = ''
          
          // Try to find the end of the first complete JSON structure
          let braceCount = 0
          let bracketCount = 0
          let inString = false
          let escapeNext = false
          
          for (let i = 0; i < jsonStart.length; i++) {
            const char = jsonStart[i]
            
            if (escapeNext) {
              escapeNext = false
              validJson += char
              continue
            }
            
            if (char === '\\' && inString) {
              escapeNext = true
              validJson += char
              continue
            }
            
            if (char === '"') {
              inString = !inString
              validJson += char
              continue
            }
            
            if (!inString) {
              if (char === '{') {
                braceCount++
              } else if (char === '}') {
                braceCount--
              } else if (char === '[') {
                bracketCount++
              } else if (char === ']') {
                bracketCount--
              }
            }
            
            validJson += char
            
            // If we've closed all braces and brackets, we have a complete JSON structure
            if (braceCount === 0 && bracketCount === 0 && (validJson.trim().startsWith('{') || validJson.trim().startsWith('['))) {
              break
            }
          }
          
          // Try parsing the extracted JSON
          try {
            parsed = JSON.parse(validJson)
            console.warn('Successfully extracted valid JSON from content')
          } catch (extractError) {
            console.warn('Could not extract valid JSON, skipping format')
            return // Exit early if we can't parse
          }
        }
        
        // Format JSON
        const formatted = JSON.stringify(parsed, null, 2)
        
        // Restore tokens - handle both quoted and unquoted contexts
        const restored = formatted.replace(/"?__TOKEN_(\d+)__"?/g, (match, index) => {
          const placeholder = `__TOKEN_${index}__`
          const originalToken = tokenMap[placeholder]
          
          if (originalToken) {
            // If the original token was a placeholder token, restore it as-is
            // If it was inside quotes, it should stay inside quotes
            if (match.startsWith('"') && match.endsWith('"')) {
              return originalToken
            } else {
              return originalToken
            }
          }
          
          return match
        })
        
        this.editor.setValue(restored)
      } catch (error) {
        console.warn('Cannot format invalid JSON:', error)
        
        // Provide more helpful error information
        if (error instanceof Error) {
          console.warn('Error details:', error.message)
          console.warn('Original content:', this.editor.getValue())
        }
      }
    },

    /**
     * Clear editor content
     */
    clearContent(): void {
      if (this.disabled || !this.editor) return
      this.editor.setValue('')
    },

    /**
     * Update read-only state
     */
    updateReadOnly(): void {
      if (this.editor) {
        this.editor.setOption('readOnly', this.disabled)
      }
    },

    /**
     * Update editor value
     */
    updateValue(newValue: string): void {
      if (this.editor && this.editor.getValue() !== newValue) {
        const cursor = this.editor.getCursor()
        this.editor.setValue(newValue)
        this.editor.setCursor(cursor)
        
        // Trigger chip refresh after value update
        setTimeout(() => {
          this.refreshTokenWidgets()
        }, 100)
      }
    },

    /**
     * Replace all tokens in text with valid JSON values for validation
     */
    replaceTokensForValidation(text: string): string {
      let validatedText = text
      
      // Replace [PLACEHOLDER_NAME] tokens with quoted strings
      validatedText = validatedText.replace(/\[PLACEHOLDER_[A-Z_]+\]/g, '"__PLACEHOLDER__"')
      
      // Replace {{fieldName}} tokens with quoted strings
      validatedText = validatedText.replace(/{{\s*[\w.\[\]_-]+\s*}}/g, '"__PLACEHOLDER__"')
      
      // Handle cases where tokens are used as property values without quotes
      validatedText = validatedText.replace(/:\s*\[PLACEHOLDER_[A-Z_]+\]/g, ': "__PLACEHOLDER__"')
      validatedText = validatedText.replace(/:\s*{{\s*[\w.\[\]_-]+\s*}}/g, ': "__PLACEHOLDER__"')
      
      // Handle tokens in arrays
      validatedText = validatedText.replace(/\[\s*\[PLACEHOLDER_[A-Z_]+\]/g, '["__PLACEHOLDER__"')
      validatedText = validatedText.replace(/\[\s*{{\s*[\w.\[\]_-]+\s*}}/g, '["__PLACEHOLDER__"')
      validatedText = validatedText.replace(/,\s*\[PLACEHOLDER_[A-Z_]+\]/g, ', "__PLACEHOLDER__"')
      validatedText = validatedText.replace(/,\s*{{\s*[\w.\[\]_-]+\s*}}/g, ', "__PLACEHOLDER__"')
      
      // Handle tokens that might be at the end of arrays
      validatedText = validatedText.replace(/\[PLACEHOLDER_[A-Z_]+\]\s*\]/g, '"__PLACEHOLDER__"]')
      validatedText = validatedText.replace(/{{\s*[\w.\[\]_-]+\s*}}\s*\]/g, '"__PLACEHOLDER__"]')
      
      return validatedText
    },

    /**
     * Check if a position in the text is within or near a field token
     */
    isNearToken(text: string, position: number): boolean {
      // Get context around the position
      const start = Math.max(0, position - 50)
      const end = Math.min(text.length, position + 50)
      const context = text.substring(start, end)
      
      // Check if context contains tokens
      return context.includes('[PLACEHOLDER_') || context.includes('{{')
    },

    /**
     * Force refresh the editor highlighting
     */
    refreshHighlighting(): void {
      if (this.editor) {
        this.editor.refresh()
        // Force re-highlight all content
        let editorMode = 'javascript' // Safe fallback
        try {
          if (CodeMirror.modes && CodeMirror.modes['json-with-tokens']) {
            editorMode = 'json-with-tokens'
          } else if (CodeMirror.getMode({}, 'application/json')) {
            editorMode = 'application/json'
          }
        } catch (e) {
          console.warn('Failed to determine CodeMirror mode, using javascript fallback:', e)
        }
        this.editor.setOption('mode', editorMode)
      }
    },

  },
  watch: {
    value(newValue: string): void {
      this.updateValue(newValue)
    },
    disabled(): void {
      this.updateReadOnly()
    },
    placeholder(newPlaceholder: string): void {
      if (this.editor) {
        this.editor.setOption('placeholder', newPlaceholder)
      }
    },
    showTokenChips(newValue: boolean): void {
      // Check if editor is initialized before proceeding
      if (!this.editor) {
        return
      }
      
      if (newValue) {
        // Enable chips - refresh to show them
        this.$nextTick(() => {
          this.refreshTokenWidgets()
        })
      } else {
        // Disable chips - clear all markers
        this.clearTokenMarkers()
      }
    }
  },
  mounted(): void {
    this.$nextTick(() => {
      this.initializeEditor()
    })
  },
  beforeDestroy(): void {
    // Clean up timeouts
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout)
    }
    if (this.cursorActivityTimeout) {
      clearTimeout(this.cursorActivityTimeout)
    }
    
    // Clean up token markers first
    this.clearTokenMarkers()

    if (this.editor) {
      // Clean up CodeMirror instance
      const element = this.editor.getWrapperElement()
      if (element && element.parentNode) {
        element.parentNode.removeChild(element)
      }
      this.editor = null
    }
  }
})
</script>

<style lang="scss" scoped>
.json-editor {
  font-family: unset !important;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  &__container {
    flex: 1;
    position: relative;
    border: 1px solid #edebeb;
    border-radius: var(--radius-sm);
    background: #ffffff;
    overflow: hidden;
    
    &--disabled {
      opacity: 0.6;
      background: #f5f5f5;
    }
    
    &--drag-over {
      border-color: #406A94;
      box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
    }
  }
}

// CodeMirror font styling - Maximum specificity to override app.vue universal selector
.json-editor :deep(.CodeMirror),
.json-editor :deep(.CodeMirror) *,
.json-editor :deep(.CodeMirror) *::before,
.json-editor :deep(.CodeMirror) *::after {
  font-family: 'Consolas', 'Monaco', 'Lucida Console', 'Liberation Mono', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono', 'Courier New', monospace !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
}

.json-editor :deep(.CodeMirror-line),
.json-editor :deep(.CodeMirror-line) *,
.json-editor :deep(.CodeMirror-line) *::before,
.json-editor :deep(.CodeMirror-line) *::after {
  font-family: 'Consolas', 'Monaco', 'Lucida Console', 'Liberation Mono', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono', 'Courier New', monospace !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
}

.json-editor :deep(.CodeMirror pre),
.json-editor :deep(.CodeMirror pre) *,
.json-editor :deep(.CodeMirror pre) *::before,
.json-editor :deep(.CodeMirror pre) *::after {
  font-family: 'Consolas', 'Monaco', 'Lucida Console', 'Liberation Mono', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono', 'Courier New', monospace !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
}

// Additional targeting for text content
.json-editor :deep(.CodeMirror-code),
.json-editor :deep(.CodeMirror-code) *,
.json-editor :deep(.CodeMirror-code) *::before,
.json-editor :deep(.CodeMirror-code) *::after,
.json-editor :deep(.cm-s-default),
.json-editor :deep(.cm-s-default) *,
.json-editor :deep(.cm-s-default) *::before,
.json-editor :deep(.cm-s-default) *::after {
  font-family: 'Consolas', 'Monaco', 'Lucida Console', 'Liberation Mono', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono', 'Courier New', monospace !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
}

// Custom color overrides for dark themes - lighter blue text
.json-editor :deep(.cm-s-material-darker) {
  .cm-string { color: #89ddff !important; } // Lighter cyan-blue for strings
  .cm-property { color: #82b1ff !important; } // Lighter blue for property names
  .cm-number { color: #f78c6c !important; } // Keep orange numbers as-is
  .cm-atom { color: #89ddff !important; } // Lighter blue for true/false/null
}

.json-editor :deep(.cm-s-dracula) {
  .cm-string { color: #8be9fd !important; } // Lighter cyan for strings
  .cm-property { color: #8be9fd !important; } // Lighter cyan for property names  
  .cm-number { color: #bd93f9 !important; } // Keep purple numbers as-is
  .cm-atom { color: #8be9fd !important; } // Lighter cyan for true/false/null
}

// === CodeMirror global theme overrides for brand ===
.json-editor :deep(.CodeMirror) {
  height: 100% !important;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', 'Menlo', 'DejaVu Sans Mono', 'Liberation Mono', monospace !important;
  font-size: 13px;
  line-height: 1.5;
  border: none;
  background: #fafafa;
  color: #37474f;
  font-weight: 400;
}

.json-editor :deep(.CodeMirror.CodeMirror-readonly) {
  background: #f5f5f5;
  color: #9e9e9e;
}

.json-editor :deep(.CodeMirror-selected) {
  background: #e3f2fd;
}

.json-editor :deep(.CodeMirror-line) {
  padding: 0 4px;
}

.json-editor :deep(.CodeMirror-gutters) {
  border-right: 1px solid #e0e0e0;
  background: #f5f5f5;
}

.json-editor :deep(.CodeMirror-linenumber) {
  color: #9e9e9e;
  padding: 0 8px;
  font-size: 12px;
}

.json-editor :deep(.CodeMirror-activeline-background) {
  background: #f0f4f8;
}

.json-editor :deep(.CodeMirror-matchingbracket) {
  color: #007bff !important;
  font-weight: bold !important;
}

.json-editor :deep(.CodeMirror-nonmatchingbracket) {
  background: rgba(220, 38, 38, 0.08);
  color: #dc2626;
}

.json-editor :deep(.CodeMirror-lint-marker.CodeMirror-lint-marker-error) {
  background: #dc2626;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  height: 16px;
  line-height: 16px;
  margin-left: 2px;
  text-align: center;
  width: 16px;
}

.json-editor :deep(.CodeMirror-lint-marker.CodeMirror-lint-marker-warning) {
  background: #ea580c;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  height: 16px;
  line-height: 16px;
  margin-left: 2px;
  text-align: center;
  width: 16px;
}

.json-editor :deep(.CodeMirror-lint-tooltip) {
  background: #0B3142;
  border: 1px solid #406A94;
  border-radius: var(--radius-sm);
  color: #fff;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 12px;
  max-width: 300px;
  padding: 8px 12px;
  position: absolute;
  z-index: 1000;
}

.json-editor :deep(.CodeMirror-lint-tooltip::before) {
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #0B3142;
  bottom: -6px;
  content: '';
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
}

.json-editor :deep(.CodeMirror-lint-message-error) {
  color: #dc2626;
}

.json-editor :deep(.CodeMirror-lint-message-warning) {
  color: #ea580c;
}

.json-editor :deep(.cm-string) {
  color: #2e7d32 !important;
}

.json-editor :deep(.CodeMirror:focus-within) {
  box-shadow: 0 0 0 1px #2196f3;
  border-color: #2196f3;
}

// Additional subtle styling for default theme
.json-editor :deep(.cm-property) {
  color: #1565c0 !important;
  font-weight: 400;
}

.json-editor :deep(.cm-number) {
  color: #d84315 !important;
}

.json-editor :deep(.cm-atom) {
  color: #7b1fa2 !important;
}

.json-editor :deep(.cm-punctuation) {
  color: #616161 !important;
}

// Field token styling - monochrome design to match drag-and-drop chips
.json-editor :deep(.cm-field-token),
.json-editor :deep(.field-token) {
  color: #757575 !important;
  background: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  padding: 2px 6px !important;
  letter-spacing: 0.01em !important;
  margin: 1px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  border-radius: var(--radius-sm) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
}

// Token chip styling for actual chip elements
.json-editor :deep(.json-token-chip) {
  display: inline-flex !important;
  align-items: center !important;
  color: #757575 !important;
  background: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  padding: 4px 8px !important;
  margin: 0 2px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  border-radius: 16px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.2s ease !important;
  cursor: pointer !important;
  white-space: nowrap !important;
  max-width: 200px !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  
  &:hover {
    border-color: #bdbdbd !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(-1px) !important;
    background-color: #f5f5f5 !important;
  }
  
  &:before {
    content: "🏷️" !important;
    margin-right: 4px !important;
    font-size: 10px !important;
    opacity: 0.7 !important;
  }
}

// Fallback styling if widget replacement fails
.json-editor :deep(.json-token-chip-fallback) {
  color: #757575 !important;
  background: #ffffff !important;
  border: 1px solid #e0e0e0 !important;
  padding: 2px 6px !important;
  margin: 0 2px !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  border-radius: var(--radius-lg) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  cursor: pointer !important;
  display: inline-block !important;
}
</style>

<!-- (All CodeMirror styling is now co-located with this component for better maintainability) -->